import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { EmptyStateComponent, PaginationComponent, TooltipComponent, EnterpriseLabels, getEnterpriseLabels, SecurityDomain } from 'lib';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faInfoCircle, faPowerOff } from '@fortawesome/free-solid-svg-icons';
import { faCircleCheck } from '@fortawesome/free-regular-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { EnterpriseApplicationDto, OrganizationAttributeName } from '../../interfaces/enterprise-application-dto';
import { EnterpriseApplicationsService } from '../../services/enterprise-applications.service';
import { ConfirmationDialogService } from '../../services/confirmation-dialog.service';
import { EnterpriseLabelsFetchService } from '../../services/enterprise-labels-fetch.service';
import { FeaturesService } from '../../services/features.service';
import { EnterpriseAccessAreasComponent } from '../enterprise-access-areas/enterprise-access-areas.component';

@Component({
    selector: 'mmoga-applications',
    templateUrl: './applications.component.html',
    standalone: true,
    imports: [CommonModule, TableModule, EmptyStateComponent, PaginationComponent, FaIconComponent, TooltipComponent, EnterpriseAccessAreasComponent],
    styleUrl: './applications.component.scss'
})
export class ApplicationsComponent implements OnInit {
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly enterpriseApplicationsService = inject(EnterpriseApplicationsService);
    private readonly confirmationDialogService = inject(ConfirmationDialogService);
    private readonly toastrService = inject(ToastrService);
    private readonly enterpriseLabelsFetchService = inject(EnterpriseLabelsFetchService);
    private readonly featuresService = inject(FeaturesService);

    enterpriseId = 0;
    tableData: EnterpriseApplicationDto[] = [];
    totalItems = 0;
    currentPage = 1;
    itemsPerPage = 10;
    isLoading = false;
    isUpdating = false;
    /** The RCM card (and its per-site table) is shown only while the RCM AI Platform feature is on. */
    isRcmAiPlatformEnabled = false;

    sortBy = 'organization.urlPrefix';
    sortOrder: 'ASC' | 'DESC' = 'ASC';

    selectedIds = new Set<number>();

    readonly attributeName = OrganizationAttributeName;
    readonly faCircleCheck = faCircleCheck;
    readonly faPowerOff = faPowerOff;
    readonly faInfoCircle = faInfoCircle;

    // Brand-aware labels: "Universal ID" for MODMED, "EHR Global ID" for Eyefinity. Defaults to MODMED until resolved.
    enterpriseLabels: EnterpriseLabels = getEnterpriseLabels(SecurityDomain.MODMED);

    @ViewChild('pagination') pagination!: PaginationComponent;

    get enterpriseLoginRequiredTooltip(): string {
        return `Enable ${this.enterpriseLabels.universalIdLabel} for this site in EMA before granting RCM AI Platform access.`;
    }

    get universalIdColumnTooltip(): string {
        return `RCM AI Platform access requires ${this.enterpriseLabels.universalIdLabel} to be enabled for the site in EMA.`;
    }

    ngOnInit(): void {
        this.enterpriseLabelsFetchService.getEnterpriseLabels().then(labels => this.enterpriseLabels = labels);
        this.activatedRoute.parent?.params.subscribe(params => {
            this.enterpriseId = Number(params['id']);
            this.loadRcmWhenEnabled();
        });
    }

    private loadRcmWhenEnabled(): void {
        this.featuresService.getFeatureFlags().subscribe({
            next: flags => {
                this.isRcmAiPlatformEnabled = flags.rcmAiPlatformEnabled;
                if (this.isRcmAiPlatformEnabled) {
                    this.loadData();
                }
            },
            error: () => this.isRcmAiPlatformEnabled = false
        });
    }

    loadData(): void {
        this.isLoading = true;
        this.enterpriseApplicationsService
            .getApplications(this.enterpriseId, this.currentPage - 1, this.itemsPerPage,
                             [OrganizationAttributeName.RCM_AI_PLATFORM_ENABLED], this.sortBy, this.sortOrder)
            .subscribe({
                next: data => {
                    this.tableData = data.content;
                    this.totalItems = data.totalElements;
                    this.isLoading = false;
                },
                error: () => {
                    this.tableData = [];
                    this.totalItems = 0;
                    this.isLoading = false;
                }
            });
    }

    onSelectItemsPerPage(itemsPerPage: number): void {
        this.itemsPerPage = itemsPerPage;
    }

    onChangeCurrentPage(currentPage: number): void {
        this.currentPage = currentPage;
        this.loadData();
    }

    lazyLoad(event: TableLazyLoadEvent): void {
        const field = typeof event.sortField === 'string' ? event.sortField : this.sortBy;
        const order: 'ASC' | 'DESC' = event.sortOrder === -1 ? 'DESC' : 'ASC';
        if (field === this.sortBy && order === this.sortOrder) {
            return;
        }
        this.sortBy = field;
        this.sortOrder = order;
        this.loadData();
    }

    /** A site can only be selected for bulk actions when its Universal ID is enabled. */
    isRowSelectable(item: EnterpriseApplicationDto): boolean {
        return this.isEnterpriseLoginEnabled(item);
    }

    isCurrentPageAllSelected(): boolean {
        const selectableRows = this.tableData?.filter(item => this.isRowSelectable(item)) ?? [];
        return selectableRows.length > 0 && selectableRows.every(item => this.selectedIds.has(item.organizationId));
    }

    get allPagesSelected(): boolean {
        return this.selectedIds.size >= this.totalItems && this.totalItems > 0;
    }

    toggleCurrentPage(checked: boolean): void {
        this.tableData?.forEach(item => {
            if (checked) {
                if (this.isRowSelectable(item)) {
                    this.selectedIds.add(item.organizationId);
                }
            } else {
                this.selectedIds.delete(item.organizationId);
            }
        });
    }

    toggleRow(item: EnterpriseApplicationDto, checked: boolean): void {
        if (checked) {
            if (this.isRowSelectable(item)) {
                this.selectedIds.add(item.organizationId);
            }
        } else {
            this.selectedIds.delete(item.organizationId);
        }
    }

    selectAllPages(): void {
        this.enterpriseApplicationsService.getAllConfirmedOrgIds(this.enterpriseId, this.totalItems)
            .subscribe({
                next: ids => ids.forEach(id => this.selectedIds.add(id)),
                error: () => this.toastrService.error('Could not select all sites. Please try again.', 'RCM AI Platform')
            });
    }

    clearSelection(): void {
        this.selectedIds.clear();
    }

    onBulkEnable(): void {
        this.confirmBulkAction(true);
    }

    onBulkDisable(): void {
        this.confirmBulkAction(false);
    }

    private confirmBulkAction(enable: boolean): void {
        if (this.isUpdating || this.selectedIds.size === 0) {
            return;
        }

        const count = this.selectedIds.size;
        const action = enable ? 'enable' : 'disable';
        this.confirmationDialogService.confirm(
            `${enable ? 'Enable' : 'Disable'} RCM AI Platform`,
            `Are you sure you want to ${action} RCM AI Platform for ${count} ${count === 1 ? 'site' : 'sites'}?`,
            `Confirm & ${enable ? 'Enable' : 'Disable'}`,
            'Cancel',
            'md'
        ).then(confirmed => {
            if (confirmed) {
                this.submitBulkAction(enable);
            }
        }).catch(() => { /* dismissed */ });
    }

    isRcmEnabled(item: EnterpriseApplicationDto): boolean {
        return item.attributes?.[OrganizationAttributeName.RCM_AI_PLATFORM_ENABLED] === 'true';
    }

    isEnterpriseLoginEnabled(item: EnterpriseApplicationDto): boolean {
        return item.enterpriseLoginEnabled === true;
    }

    /** Granting RCM AI Platform access is gated on Enterprise Login (EMA-232985). Disabling is always allowed. */
    private isEnableBlocked(item: EnterpriseApplicationDto): boolean {
        return !this.isRcmEnabled(item) && !this.isEnterpriseLoginEnabled(item);
    }

    isToggleDisabled(item: EnterpriseApplicationDto): boolean {
        return this.isUpdating || this.isEnableBlocked(item);
    }

    toggleTooltip(item: EnterpriseApplicationDto): string | null {
        return this.isEnableBlocked(item) ? this.enterpriseLoginRequiredTooltip : null;
    }

    onToggleFirmStatus(item: EnterpriseApplicationDto): void {
        if (this.isUpdating || this.isEnableBlocked(item)) {
            return;
        }
        const targetEnabled = !this.isRcmEnabled(item);
        const action = targetEnabled ? 'enable' : 'disable';
        this.confirmationDialogService.confirm(
            `${targetEnabled ? 'Enable' : 'Disable'} RCM AI Platform`,
            `Are you sure you want to ${action} RCM AI Platform for ${item.siteUrl}?`,
            `Confirm & ${targetEnabled ? 'Enable' : 'Disable'}`,
            'Cancel',
            'md'
        ).then(confirmed => {
            if (confirmed) {
                this.executeToggleFirmStatus(item, targetEnabled);
            }
        }).catch(() => { /* dismissed */ });
    }

    private executeToggleFirmStatus(item: EnterpriseApplicationDto, targetEnabled: boolean): void {
        if (this.isUpdating) {
            return;
        }
        this.setRcmFlag(item, targetEnabled);
        this.isUpdating = true;

        this.enterpriseApplicationsService.setApplicationAttribute(this.enterpriseId, {
            organizationIds: [item.organizationId],
            attributeName: OrganizationAttributeName.RCM_AI_PLATFORM_ENABLED,
            value: String(targetEnabled)
        }).subscribe({
            next: () => {
                this.toastrService.success(`${item.siteUrl} ${targetEnabled ? 'enabled' : 'disabled'}.`, 'RCM AI Platform');
                this.isUpdating = false;
            },
            error: err => {
                this.setRcmFlag(item, !targetEnabled);
                this.isUpdating = false;
                this.toastrService.error(this.extractErrorMessage(err), 'RCM AI Platform');
            }
        });
    }

    private setRcmFlag(item: EnterpriseApplicationDto, enabled: boolean): void {
        item.attributes = {
            ...item.attributes,
            [OrganizationAttributeName.RCM_AI_PLATFORM_ENABLED]: String(enabled)
        };
    }

    private submitBulkAction(enable: boolean): void {
        if (this.isUpdating || this.selectedIds.size === 0) {
            return;
        }

        const ids = Array.from(this.selectedIds);
        this.isUpdating = true;

        this.enterpriseApplicationsService.setApplicationAttribute(this.enterpriseId, {
            organizationIds: ids,
            attributeName: OrganizationAttributeName.RCM_AI_PLATFORM_ENABLED,
            value: String(enable)
        }).subscribe({
            next: () => {
                this.toastrService.success(`RCM AI Platform ${enable ? 'enabled' : 'disabled'} for ${ids.length} ${ids.length === 1 ? 'site' : 'sites'}.`, 'RCM AI Platform');
                this.selectedIds.clear();
                this.isUpdating = false;
                this.loadData();
            },
            error: err => {
                this.isUpdating = false;
                this.toastrService.error(this.extractErrorMessage(err), 'RCM AI Platform');
            }
        });
    }

    private extractErrorMessage(err: unknown): string {
        const fallback = 'Something went wrong. Please try again.';
        if (typeof err === 'object' && err !== null) {
            const errorBody = (err as { error?: { message?: string } }).error;
            if (errorBody?.message) {
                return errorBody.message;
            }
        }
        return fallback;
    }
}
